// Coupon API functionality for fetching available coupons
class CouponAPI {
  constructor() {
    this.apiUrl =
      "https://apibackend.wellbeingnutrition.com/api/coupons/v2/get-coupons";
    // this.apiUrl = "https://c0bdf9fa4141.ngrok-free.app/api/coupons/get-coupons"
    this.isLoading = false;
  }

  // Get current cart data from Shopify
  async getCartData() {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) {
        throw new Error("Failed to fetch cart data");
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching cart data:", error);
      return null;
    }
  }

  // Get customer ID from local storage or session
  getCustomerId() {
    try {
      // Try to get from AuthStorage (if it exists)
      if (
        typeof AuthStorage !== "undefined" &&
        AuthStorage.getUserCredentials
      ) {
        return AuthStorage.getUserCredentials()?.shopifyCustomerId || "";
      }

      // Fallback: try to get from localStorage
      const userData =
        localStorage.getItem("userCredentials") ||
        localStorage.getItem("customer_data");
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.shopifyCustomerId || parsed.customer_id || "";
      }

      // Another fallback: check for Shopify customer object
      if (typeof window.customer !== "undefined" && window.customer?.id) {
        return window.customer.id.toString();
      }

      return "";
    } catch (error) {
      console.error("Error getting customer ID:", error);
      return "";
    }
  }

  // Transform Shopify cart data to API format
  transformCartToAPIFormat(cart) {
    if (!cart || !cart.items) {
      return {
        totalPrice: 0,
        customer_id: this.getCustomerId(),
        line_items: [],
      };
    }

    const lineItems = cart.items.map((item) => {
      // Get SKU from variant if available
      let sku = "";
      if (item.sku) {
        sku = item.sku;
      } else if (item.variant_id) {
        // If SKU is not directly available, we'll use variant_id as fallback
        sku = item.variant_id.toString();
      }

      return {
        sku: sku,
        product_id: item.product_id?.toString() || "",
        variant_id: item.variant_id?.toString() || "",
        amount: parseFloat(item.price / 100) || 0, // Convert from cents to dollars
        quantity: item.quantity || 0,
        tags: item.product_tags || [], // Product tags from cart item
      };
    });

    return {
      totalPrice: parseFloat(cart.total_price / 100) || 0, // Convert from cents to dollars
      customer_id: this.getCustomerId(),
      line_items: lineItems,
    };
  }

  // Fetch coupons from API
  async fetchCoupons() {
    if (this.isLoading) {
      return null;
    }

    this.isLoading = true;

    try {
      // Show loading state
      this.showLoadingState();

      // Get current cart data
      const cart = await this.getCartData();
      if (!cart) {
        throw new Error("Unable to fetch cart data");
      }

      // Transform cart data to API format
      const requestBody = this.transformCartToAPIFormat(cart);


      // Make API call
      const response = await fetch(this.apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`API request failed with status: ${response.status}`);
      }

      const data = await response.json();

      // Update coupon display
      this.updateCouponsDisplay(data);

      return data;
    } catch (error) {
      console.error("Error fetching coupons:", error);
      this.showErrorState(error.message);
      return null;
    } finally {
      this.isLoading = false;
      this.hideLoadingState();
    }
  }

  // Show loading state in coupon section
  showLoadingState() {
    const couponContent = document.querySelector(".coupon-content");
    if (couponContent) {
      // Clear existing coupon sections (keep the back button and apply coupon input)
      const existingSections =
        couponContent.querySelectorAll(".coupon-section");
      existingSections.forEach((section) => section.remove());

      // Add single loading indicator
      const loadingHTML = `
        <div class="coupon-loading-state" style="text-align: center; padding: 40px;">
          <div style="display: inline-block; width: 24px; height: 24px; border: 3px solid #f3f3f3; border-top: 3px solid #000; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 15px;"></div>
          <p style="margin: 0; color: #666;">Loading coupons...</p>
        </div>
        <style>
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      `;

      // Add loading section
      couponContent.innerHTML += loadingHTML;
    }
  }

  // Hide loading state
  hideLoadingState() {
    const loadingState = document.querySelector(".coupon-loading-state");
    if (loadingState) {
      loadingState.remove();
    }
  }

  // Show error state
  showErrorState(errorMessage) {
    const couponSection = document.querySelector(".coupon-section");
    if (couponSection) {
      const errorHTML = `
        <div class="coupon-error-state" style="text-align: center; padding: 40px;">
          <div style="color: #ff4444; font-size: 18px; margin-bottom: 10px;">⚠️</div>
          <h3 style="color: #ff4444; margin: 0 0 10px; font-size: 16px;">Unable to load coupons</h3>
          <p style="margin: 0; color: #666; font-size: 14px;">${errorMessage}</p>
          <button onclick="window.couponAPI.fetchCoupons()" style="margin-top: 15px; padding: 8px 16px; background: #000; color: #fff; border: none; border-radius: 4px; cursor: pointer;">
            Try Again
          </button>
        </div>
      `;

      const availableCouponsSection =
        couponSection.querySelector(".available-coupons-section-title")
          ?.parentElement || couponSection;
      availableCouponsSection.innerHTML = errorHTML;
    }
  }

  // Update coupons display with API response
  updateCouponsDisplay(apiResponse) {
    // Use the render function from coupon-drawer.liquid
    if (typeof window.renderCouponsFromAPI === "function") {
      window.renderCouponsFromAPI(apiResponse);

      // After rendering coupons, initialize the coupon drawer state
      if (typeof window.initializeCouponDrawer === "function") {
        window.initializeCouponDrawer();
      }
    } else {
      console.error("renderCouponsFromAPI function not available");
      this.showErrorState("Coupon rendering function not available");
    }
  }

  // Show empty state when no coupons available
  showEmptyState() {
    // Use the render function with empty data
    if (typeof window.renderCouponsFromAPI === "function") {
      window.renderCouponsFromAPI({ data: [] });
    } else {
      console.error("renderCouponsFromAPI function not available");
    }
  }
}

// Initialize the coupon API
window.couponAPI = new CouponAPI();

// Global function to apply coupon (called from coupon buttons)
function applyCoupon(code) {
  if (!code) {
    alert("Invalid coupon code");
    return;
  }


  // Here you can add logic to apply the coupon to Shopify cart
  // For now, we'll show a success message
  alert(`Coupon ${code} applied successfully!`);

  // You may want to close the coupon drawer and refresh the cart
  if (typeof toggleCouponView === "function") {
    toggleCouponView();
  }
}

// Global function to toggle coupon terms
function toggleCouponTerms(element) {
  const termsDiv = element.previousElementSibling;
  if (termsDiv && termsDiv.classList.contains("coupon-terms")) {
    const isExpanded = termsDiv.style.display === "block";
    termsDiv.style.display = isExpanded ? "none" : "block";
    element.textContent = isExpanded ? "Know more" : "Know less";
  }
}

// Function to fetch coupons when "View All Coupons" is clicked
function fetchAvailableCoupons() {
  if (window.couponAPI) {
    window.couponAPI.fetchCoupons();
  } else {
    console.error("Coupon API not initialized");
  }
}

// Make the function globally available
window.fetchAvailableCoupons = fetchAvailableCoupons;
